import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
  VStack,
  useBreakpointValue,
  useToast,
} from "@chakra-ui/react";
import moment from "moment";
import { useAuth } from "../providers/AuthProvider";
import {
  buildEvaluationContext,
  fetchEvaluationResponse,
  fetchFullCertificationName,
  fetchPublishedEvaluationForm,
  formatEvaluationOpensLabel,
  isEvaluationOpenForCourseRun,
  normalizeEvaluationSchema,
  seedAnswersFromContext,
  submitEvaluationResponse,
  validateEvaluationAnswers,
} from "../utils/lmsEvaluations";

function displayBoundValue(field, value) {
  if (value == null || value === "") return "—";
  if (field.type === "name" && value && typeof value === "object") {
    return [value.first, value.last].filter(Boolean).join(" ") || "—";
  }
  return String(value);
}

function BoundValue({ children }) {
  return (
    <Text
      fontSize="md"
      fontWeight="600"
      color="gray.800"
      py={1}
      borderBottomWidth="1px"
      borderColor="gray.100"
    >
      {children}
    </Text>
  );
}

function RatingMatrixField({ field, value, onChange, readOnly }) {
  const map = value && typeof value === "object" ? value : {};
  const scale = field.scale || [];
  const useStacked = useBreakpointValue({ base: true, md: false });

  if (useStacked) {
    return (
      <VStack align="stretch" spacing={4}>
        {(field.rows || []).map((row) => (
          <Box
            key={row.id}
            p={3}
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="lg"
            bg="gray.50"
          >
            <Text fontSize="sm" fontWeight="600" mb={3} color="gray.800">
              {row.label}
            </Text>
            <VStack align="stretch" spacing={2}>
              {scale.map((label) => {
                const selected = map[row.id] === label;
                return (
                  <Box
                    key={label}
                    as="button"
                    type="button"
                    disabled={readOnly}
                    onClick={() => !readOnly && onChange({ ...map, [row.id]: label })}
                    textAlign="left"
                    px={3}
                    py={3}
                    minH="44px"
                    borderRadius="md"
                    borderWidth="2px"
                    borderColor={selected ? "blue.500" : "gray.200"}
                    bg={selected ? "blue.50" : "white"}
                    fontWeight={selected ? "700" : "500"}
                    fontSize="sm"
                    color={selected ? "blue.800" : "gray.700"}
                    _disabled={{ opacity: 0.7, cursor: "not-allowed" }}
                  >
                    {label}
                  </Box>
                );
              })}
            </VStack>
          </Box>
        ))}
      </VStack>
    );
  }

  return (
    <Box overflowX="auto">
      <Box as="table" w="100%" minW="520px" borderCollapse="collapse" fontSize="sm">
        <Box as="thead">
          <Box as="tr">
            <Box as="th" borderWidth="1px" p={2} textAlign="left" bg="gray.50">
              Item
            </Box>
            {scale.map((label) => (
              <Box
                as="th"
                key={label}
                borderWidth="1px"
                p={2}
                textAlign="center"
                bg="gray.50"
                fontSize="xs"
              >
                {label}
              </Box>
            ))}
          </Box>
        </Box>
        <Box as="tbody">
          {(field.rows || []).map((row) => (
            <Box as="tr" key={row.id}>
              <Box as="td" borderWidth="1px" p={2} fontSize="xs">
                {row.label}
              </Box>
              {scale.map((label) => (
                <Box as="td" key={label} borderWidth="1px" p={2} textAlign="center">
                  <input
                    type="radio"
                    name={`${field.id}_${row.id}`}
                    value={label}
                    disabled={readOnly}
                    checked={map[row.id] === label}
                    aria-label={`${row.label}: ${label}`}
                    style={{ width: 18, height: 18 }}
                    onChange={() => onChange({ ...map, [row.id]: label })}
                  />
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function FieldInput({ field, value, onChange, readOnly }) {
  const bound = Boolean(field.bindTo);
  const locked = readOnly || bound;

  if (bound && !readOnly) {
    return <BoundValue>{displayBoundValue(field, value)}</BoundValue>;
  }

  if (field.type === "textarea") {
    return (
      <Textarea
        value={value || ""}
        isReadOnly={locked}
        isDisabled={locked && readOnly}
        bg={locked ? "gray.50" : "white"}
        rows={4}
        fontSize={{ base: "md", md: "sm" }}
        placeholder={field.placeholder || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (field.type === "select") {
    return (
      <Select
        value={value || ""}
        isDisabled={locked}
        bg={locked ? "gray.50" : "white"}
        placeholder="Please select"
        size="lg"
        fontSize="md"
        onChange={(e) => onChange(e.target.value)}
      >
        {(field.options || []).map((opt) => (
          <option key={opt.id || opt.label} value={opt.label}>
            {opt.label}
          </option>
        ))}
      </Select>
    );
  }

  if (field.type === "name") {
    if (readOnly) {
      return <BoundValue>{displayBoundValue(field, value)}</BoundValue>;
    }
    const nameVal = value && typeof value === "object" ? value : { first: "", last: "" };
    return (
      <Stack direction={{ base: "column", sm: "row" }} spacing={3}>
        <Input
          value={nameVal.first || ""}
          size="lg"
          placeholder="First name"
          onChange={(e) => onChange({ ...nameVal, first: e.target.value })}
        />
        <Input
          value={nameVal.last || ""}
          size="lg"
          placeholder="Last name"
          onChange={(e) => onChange({ ...nameVal, last: e.target.value })}
        />
      </Stack>
    );
  }

  if (field.type === "rating_matrix") {
    return (
      <RatingMatrixField
        field={field}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
      />
    );
  }

  if (readOnly) {
    return <BoundValue>{displayBoundValue(field, value)}</BoundValue>;
  }

  const inputType =
    field.type === "date" ? "date" : field.type === "phone" ? "tel" : "text";

  return (
    <Input
      type={inputType}
      value={value || ""}
      size="lg"
      fontSize="md"
      placeholder={field.placeholder || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/**
 * Student course evaluation form (LMS).
 */
export function CourseEvaluationTab({
  courseRun,
  courseRunId,
  registration,
  onSubmitted,
}) {
  const { user } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(null);
  const [existing, setExisting] = useState(null);
  const [answers, setAnswers] = useState({});
  const [context, setContext] = useState({});

  const schema = useMemo(() => {
    if (existing?.form_snapshot?.schema) {
      return normalizeEvaluationSchema(existing.form_snapshot.schema);
    }
    return normalizeEvaluationSchema(form?.schema);
  }, [form, existing]);

  const title = existing?.form_snapshot?.title || form?.title || "Course Evaluation";
  const description =
    existing?.form_snapshot?.description ||
    form?.description ||
    "Share your feedback to improve the course experience.";
  const readOnly = Boolean(existing);
  const isOpen = form ? isEvaluationOpenForCourseRun(form, courseRun) : false;
  const opensLabel = form ? formatEvaluationOpensLabel(form, courseRun) : null;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const published = await fetchPublishedEvaluationForm();
        if (cancelled) return;
        setForm(published);

        if (!published || !registration?.id || !courseRunId) {
          setExisting(null);
          setAnswers({});
          setContext({});
          return;
        }

        const response = await fetchEvaluationResponse({
          formId: published.id,
          registrationId: registration.id,
          courseRunId,
        });
        if (cancelled) return;

        if (response) {
          setExisting(response);
          setAnswers(response.answers || {});
          setContext(response.context_snapshot || {});
          return;
        }

        const fullCertificationName = await fetchFullCertificationName(registration);
        if (cancelled) return;
        const ctx = buildEvaluationContext({
          courseRun,
          registration,
          fullCertificationName,
        });
        const seeded = seedAnswersFromContext(
          normalizeEvaluationSchema(published.schema),
          ctx
        );
        normalizeEvaluationSchema(published.schema).sections.forEach((section) => {
          section.fields.forEach((field) => {
            if (field.type === "date" && !seeded[field.id]) {
              seeded[field.id] = moment().format("YYYY-MM-DD");
            }
          });
        });
        setExisting(null);
        setContext(ctx);
        setAnswers(seeded);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [courseRun, courseRunId, registration?.id]);

  const setFieldAnswer = (fieldId, value) => {
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async () => {
    if (!form || !registration?.id || !courseRunId) return;
    if (!isEvaluationOpenForCourseRun(form, courseRun)) {
      toast({
        title: "Evaluation not open yet",
        description: formatEvaluationOpensLabel(form, courseRun) || "Please check back later.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    const missing = validateEvaluationAnswers(schema, answers);
    if (missing.length > 0) {
      toast({
        title: "Please complete required fields",
        description: missing.slice(0, 4).join(", "),
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setSubmitting(true);
    try {
      const saved = await submitEvaluationResponse({
        form,
        registrationId: registration.id,
        courseRunId,
        userId: registration.user_id || user?.id || null,
        answers,
        contextSnapshot: context,
      });
      setExisting({
        id: saved.id,
        form_id: form.id,
        registration_id: registration.id,
        course_run_id: courseRunId,
        form_snapshot: {
          title: form.title,
          description: form.description,
          schema: form.schema,
          settings: form.settings,
        },
        answers,
        context_snapshot: context,
        submitted_at: saved.submitted_at,
      });
      toast({
        title: "Thank you!",
        description: "Your course evaluation has been submitted.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      if (typeof onSubmitted === "function") onSubmitted();
    } catch (err) {
      console.error(err);
      const msg = err?.message || "Could not submit evaluation.";
      const isDuplicate =
        String(msg).toLowerCase().includes("duplicate") ||
        err?.code === "23505";
      toast({
        title: isDuplicate ? "Already submitted" : "Submit failed",
        description: isDuplicate
          ? "You have already submitted an evaluation for this course."
          : msg,
        status: isDuplicate ? "info" : "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box py={10} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (!form && !existing) {
    return (
      <Alert status="info" borderRadius="lg">
        <AlertIcon />
        The course evaluation form is not open yet. Please check back later.
      </Alert>
    );
  }

  if (!registration?.id) {
    return (
      <Alert status="warning" borderRadius="lg">
        <AlertIcon />
        We could not match your enrolment for this course, so evaluation is unavailable.
        If you are staff, use View as student on Home first, then open the course again.
      </Alert>
    );
  }

  if (!readOnly && form && !isOpen) {
    return (
      <Alert status="info" borderRadius="lg">
        <AlertIcon />
        {opensLabel || "Evaluation is not open yet. Please check back later."}
      </Alert>
    );
  }

  return (
    <VStack align="stretch" spacing={5}>
      {readOnly ? (
        <Alert status="success" borderRadius="lg">
          <AlertIcon />
          You submitted this evaluation
          {existing?.submitted_at
            ? ` on ${moment(existing.submitted_at).format("DD MMM YYYY")}`
            : ""}
          . Answers below are from your submission.
        </Alert>
      ) : (
        <Box>
          <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="700" color="gray.800" mb={1}>
            {title}
          </Text>
          {description ? (
            <Text fontSize="sm" color="gray.600">
              {description}
            </Text>
          ) : null}
        </Box>
      )}

      {schema.sections.map((section) => (
        <Box
          key={section.id}
          p={{ base: 4, md: 5 }}
          bg="white"
          borderRadius="xl"
          borderWidth="1px"
          borderColor="gray.200"
        >
          <Text fontWeight="700" mb={1} fontSize={{ base: "md", md: "lg" }}>
            {section.title}
          </Text>
          {section.description ? (
            <Text fontSize="sm" color="gray.600" mb={4}>
              {section.description}
            </Text>
          ) : (
            <Box mb={3} />
          )}
          <Stack spacing={5}>
            {section.fields.map((field) => (
              <FormControl key={field.id} isRequired={field.required && !readOnly && !field.bindTo}>
                <FormLabel fontSize="sm" mb={1}>
                  {field.label}
                </FormLabel>
                {field.helpText ? (
                  <FormHelperText mt={0} mb={2}>
                    {field.helpText}
                  </FormHelperText>
                ) : null}
                <FieldInput
                  field={field}
                  value={answers[field.id]}
                  readOnly={readOnly}
                  onChange={(next) => setFieldAnswer(field.id, next)}
                />
              </FormControl>
            ))}
          </Stack>
        </Box>
      ))}

      {!readOnly ? (
        <Button
          colorScheme="blue"
          size="lg"
          w={{ base: "100%", md: "auto" }}
          alignSelf={{ base: "stretch", md: "flex-start" }}
          isLoading={submitting}
          onClick={handleSubmit}
        >
          Submit evaluation
        </Button>
      ) : null}
    </VStack>
  );
}
